import React, { useRef, useState, useEffect } from 'react'

import dayjs from 'dayjs'
import classNames from 'classnames'

import { Slider, SliderSingleProps, Space, Button } from 'antd'
import { PlayCircleTwoTone, PauseCircleTwoTone, CloseCircleTwoTone } from '@ant-design/icons'

import style from './css/PlayControler.module.scss'

interface PlayControlerProp {
  src: string
  onPlayControlChange?: (time: number) => void
  audioTime?: number
  mobileMode?: boolean
  className?: string
  sliderProps?: SliderSingleProps
  autoPlay?: boolean
  onClose?: () => void
  onEnded?: () => void
}

const PlayControler: React.FC<PlayControlerProp> = ({
  src,
  onPlayControlChange,
  audioTime: propAudioTime = 0,
  mobileMode,
  className,
  sliderProps = {},
  autoPlay,
  onClose,
  onEnded
}) => {
  const { className: sliderClassName, ...restSliderProps } = sliderProps
  const [playSate, setPlayState] = useState(false)

  const [audioDuration, setAudioDuration] = useState(0)

  const [audioTime, setAudioTime] = useState(propAudioTime)

  const audioRef = useRef<HTMLAudioElement>(null)

  useEffect(() => {
    setAudioTime(propAudioTime)
    if (audioRef.current) {
      audioRef.current.currentTime = propAudioTime
    }
  }, [propAudioTime])

  useEffect(() => {
    if (audioRef.current) {
      setAudioTime(0)
      audioRef.current.currentTime = 0
      if (autoPlay) {
        play()
      }
    }
  }, [autoPlay, src])

  const handleSliderChange: SliderSingleProps['onChange'] = value => {
    setAudioTime(value)
    if (audioRef.current) {
      audioRef.current.currentTime = value
    }
    onPlayControlChange && onPlayControlChange(value)
  }

  const handleAudioLoadedMetadataCapture = (e: React.SyntheticEvent<HTMLAudioElement, Event>) => {
    setAudioDuration((e.nativeEvent.target as HTMLAudioElement).duration)
  }

  const handleAudioTimeUpdate = (e: React.SyntheticEvent<HTMLAudioElement, Event>) => {
    setAudioTime((e.target as HTMLAudioElement).currentTime)
  }

  const handleAudioEnded = () => {
    setPlayState(false)
    onEnded && onEnded()
  }

  const setAudioPlayRate = (rate: number) => {
    if (audioRef.current) {
      audioRef.current.playbackRate = rate
    }
  }

  const pause = () => {
    audioRef.current?.pause()
  }

  const play = () => {
    audioRef.current?.play()
  }

  return (
    <div className={classNames(style['custom-player'], className)}>
      <Slider
        step={0.001}
        vertical={Boolean(mobileMode)}
        reverse={Boolean(mobileMode)}
        max={audioDuration}
        value={audioTime}
        tooltip={{ open: false }}
        onChange={handleSliderChange}
        className={classNames(style.slider, sliderClassName, { [style.vertical]: Boolean(mobileMode) })}
        {...restSliderProps}
      />

      <audio
        // controls
        preload="metadata"
        ref={audioRef}
        onLoadedMetadataCapture={handleAudioLoadedMetadataCapture}
        onTimeUpdate={handleAudioTimeUpdate}
        onEnded={handleAudioEnded}
        src={src}
        onPause={() => {
          setPlayState(false)
        }}
        onPlay={() => {
          setPlayState(true)
        }}
      />
      <div className={style['player-control']}>
        <div className={style['player-control-left']}>
          <span className={style['player-control-left-current']}>
            {dayjs(audioTime * 1000 - 28800000).format('HH:mm:ss')}
          </span>
          /
          <span className={style['player-control-left-duration']}>
            {dayjs(audioDuration * 1000 - 28800000).format('HH:mm:ss')}
          </span>
        </div>
        <div
          className={style['player-control-center']}
          onClick={() => {
            playSate ? pause() : play()
          }}
          style={{ fontSize: '40px' }}
        >
          {playSate ? <PauseCircleTwoTone /> : <PlayCircleTwoTone />}
        </div>
        <div className={style['player-control-right']}>
          <Space size="small">
            {[1, 1.25, 1.5, 2].map(item => (
              <Button
                key={item}
                size="small"
                type="primary"
                onClick={() => {
                  setAudioPlayRate(item)
                }}
              >
                x {item}
              </Button>
            ))}
          </Space>
        </div>
      </div>
      {onClose && <CloseCircleTwoTone className={style['close-icon']} onClick={onClose} />}
    </div>
  )
}

export { PlayControler }
